<template>
  <div class="zone-map">
    <filters @search="applyFilter" class="filter-panel"/>
    <i><small> The tonnage zone map shows current tonnage in the zone, as well as the development for the selected
      period. <br>
      The green/red arrows shows the change from today compared to the beginning of the selected period. <br>
      Timeseries graph is converted to weekly data when last 90 days or last year is selected.
    </small></i>
    <zone-map
      :stats="formattedStats"
      :zones="Object.values(formattedZones)"
      :percentage="apiFilterValues.percentage"
      :mini-charts="apiFilterValues.miniCharts"
      @show-graph="handleShowGraphModal"/>

    <b-modal-custom
      title="Vessel count"
      :modal-class.sync="$options.data.themeClass"
      v-model="graphModal.show"
      size="lg"
      :ok-only="true"
      ok-title="Close">
      <timeseries-chart
        :title="graphModal.title"
        :loading="graphModal.loading"
        :dates="graphModal.zoneStats.date"
        :values="graphModal.zoneStats.vesselCount"
        :export-loading="graphModal.exportLoading"
        :export-types="graphModal.exportTypes"
        @ok="evt => handleCloseGraphModal"
        @export="format => handleExport(format)"/>

      <template #modal-footer>
        <b-button @click="vesselsModal.show = true" variant="info">
          Display List Of Vessels
        </b-button>

        <b-button @click="graphModal.show = false" variant="secondary">
          Close
        </b-button>
      </template>
    </b-modal-custom>

    <vessels-modal
      v-model="vesselsModal.show"
      :filter-values="vesselsFilterValues"/>
  </div>
</template>

<script>
import moment from 'moment'
import getNested from 'lodash-es/get'
import isEqual from 'lodash-es/isEqual'
import pickBy from 'lodash-es/pickBy'
import { mapActions } from 'vuex'
import Filters from '../components/tonnagezonemap/Filters'
import ZoneMap from '../components/tonnagezonemap/ZoneMap'
import TimeseriesChart from '../components/widgets/TimeseriesChart'
import { downloadAsFileByURL } from '@/shared/download-utils'
import VesselsModal from '../components/tonnagezones/VesselsModal'
import BModalCustom from '@/base_components/BModalCustom'

export default {
  name: 'tonnage-zone-map',

  components: {
    BModalCustom,
    VesselsModal,
    TimeseriesChart,
    Filters,
    ZoneMap,
  },

  data() {
    return {
      errors: [],
      stats: [],
      zones: [],
      apiFilterValues: {},
      requiredAPIFieldNames: [
        'segment',
        'direction',
        'ladenStatus',
        'period',
      ],
      graphModal: {
        show: false,
        loading: false,
        title: '',
        exportLoading: false,
        exportTypes: ['csv'],
        zoneId: null,
        zoneStats: {date: [], vesselCount: []},
        valuesCount: null,  // "31" to display the same graph as on small cards
      },
      vesselsModal: {
        show: false,
      }
    };
  },

  async created() {
    await this.loadZonesPolygons();
  },

  methods: {
    ...mapActions(
      'api/tonnage', [
        'getTonnageZonesWithPolygons',
        'getTonnageZonesGlobalStat',
        'getTonnageBalanceZonesStats',
      ]
    ),

    async applyFilter(apiFilterValues) {
      if(this.isAPIRequestRequired(apiFilterValues, this.apiFilterValues)) {
        this.apiFilterValues = apiFilterValues;
        await this.loadZonesStat(apiFilterValues);
      } else {
        // it's not required to retrieve API response this case
        // but it's needed to reload stats
        // otherwise graph markers won't fire @ready events and graphs won't be displayed this case
        this.apiFilterValues = apiFilterValues;
        let statsBackup = this.stats;
        this.stats = [];
        this.$nextTick(() => {
          this.stats = statsBackup
        });
      }
    },

    isAPIRequestRequired(origObj1, origObj2) {
      let obj1 = this.filterParametersByNames(origObj1, this.requiredAPIFieldNames);
      let obj2 = this.filterParametersByNames(origObj2, this.requiredAPIFieldNames);
      return !isEqual(obj1, obj2);
    },

    async loadZonesPolygons() {
      let zones = await this.getTonnageZonesWithPolygons();
      if(zones.error) {
        this.errors.push(zones.error);
        return;
      }
      this.zones = zones.zones;
    },

    async loadZonesStat(apiFilterValues) {
      this.stats = [];

      let stats = await this.getTonnageZonesGlobalStat(apiFilterValues);
      if(stats.error) {
        this.errors.push(stats.error);
        return;
      }
      this.stats = stats;
    },

    async handleShowGraphModal(zone) {
      let apiData = {
        ...this.apiFilterValues,
        zoneId: [zone.zoneId],
      };

      this.graphModal = {
        ...this.graphModal,
        zoneStats: {date: [], vesselCount: []},
        zoneId: zone.zoneId,
        title: zone.zoneName,
        show: true,
        loading: true,
      };

      let response = await this.getTonnageBalanceZonesStats(apiData) || [];
      let statItems = getNested(response, 'data.tonnageZoneCounts', [])
      if(this.graphModal.valuesCount) {
        statItems = statItems.slice(-this.graphModal.valuesCount);
      }

      statItems.forEach(item => {
        this.graphModal.zoneStats.date.push(item.date);
        this.graphModal.zoneStats.vesselCount.push(item.vesselCount);
      });
      this.graphModal.loading = false;
    },

    async handleCloseGraphModal() {
      this.graphModal.show = false;
    },

    async handleExport(format) {
      window.analytics.track('TonnageZoneMapLoad');
      this.graphModal.exportLoading = true;
      let response = await this.getTonnageBalanceZonesStats({
        ...this.filterParameters,
        format,
      });
      const timestamp = moment().format('YYYY-MM-DD-HH-mm');
      downloadAsFileByURL(
        getNested(response, 'data.csv', null),
        `tonnage_zone_export_${timestamp}.${format}`
      );
      this.graphModal.exportLoading = false;
    },

    filterParametersByNames(filterParameters, requiredFields) {
      // removes redundant parameters
      // (keeps only requiredFields from filterParameters)
      return pickBy(filterParameters, (value, name) => {
        return requiredFields.includes(name);
      });
    },
  },

  computed: {
    formattedStats() {
      let formattedStats = {};

      this.stats.forEach(item => {
        const zoneId = item.zoneId;
        if(formattedStats[zoneId] === undefined) {
          formattedStats[zoneId] = {
            zoneId: zoneId,
            name: item.zoneName,
            date: [],
            avgSpeed: [],
            vesselCount: [],
            vesselDwt: [],
            zone: this.formattedZones[zoneId],
          };
        }
        let statItem = formattedStats[zoneId];
        statItem.date.push(item.date || '--');
        statItem.avgSpeed.push(item.avgSpeed || 0);
        statItem.vesselCount.push(item.vesselCount || 0);
        statItem.vesselDwt.push(item.vesselDwt || 0);
      });

      return formattedStats;
    },

    formattedZones() {
      let formattedZones = {};
      this.zones.forEach(zone => {
        formattedZones[zone.zoneId] = {...zone};
        Object.keys(formattedZones[zone.zoneId]).forEach(fieldName => {
          // parse all geojson fields to objects
          if(fieldName.endsWith('Geojson')) {
            formattedZones[zone.zoneId][fieldName] = JSON.parse(formattedZones[zone.zoneId][fieldName]);
          }

          // remove all wkt fields
          if(fieldName.endsWith('Wkt')) {
            delete formattedZones[zone.zoneId][fieldName];
          }
        });
      });
      return formattedZones;
    },

    vesselsFilterValues() {
      const requiredFields = ['segment', 'direction', 'ladenStatus'];
      return {
        ...this.filterParametersByNames(this.apiFilterValues, requiredFields),
        zoneId: [this.graphModal.zoneId],
      };
    },
  },
}
</script>

<style scoped lang="scss">
.filters-panel {
  z-index: 550;
}

.zone-map {
  display: flex;
  flex-direction: column;
  justify-content: stretch;

  height: calc(100vh - 140px);
}
</style>
